/**
 * Server-Killfeed-Panel: frei verschiebbares Overlay, das den Agent-Killfeed für
 * GENAU EINEN Server zeigt (nicht global wie Agent Live). Eigene, per `server_id`
 * gefilterte SSE-Subscription (`/agent/live/events?server_id=…`) + `buildKillfeedItems`
 * — dieselbe Korrelation wie Agent Live, keine Doppel-Logik. Position pro Server in
 * localStorage, geclampt, am Drag-Ende gespeichert, mit „Zurücksetzen“.
 *
 * Reine Logik (Clamp/Key/Filter) in `../killfeed-panel.ts`; Killfeed-Korrelation
 * in `../agent-live.ts`. Hier nur Drag/DOM/EventSource.
 */

import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { hasPermission } from "../store";
import { t } from "../i18n";
import { AgentLiveClient } from "../agent-live-client";
import {
  applyAgentEvent,
  applyBackfill,
  applyHeartbeat,
  applyReady,
  applyStatus,
  buildKillfeedItems,
  buildLiveUrl,
  formatClock,
  formatKillfeedText,
  initialAgentLiveState,
  type AgentLiveState,
  type KillfeedItem,
  type KillfeedLabels,
  type LiveStatus,
} from "../agent-live";
import {
  clampPanelPosition,
  defaultPanelPosition,
  filterEventsForServer,
  killfeedPanelStorageKey,
  parsePanelPosition,
  serializePanelPosition,
  KILLFEED_PANEL_DEFAULT_H,
  KILLFEED_PANEL_DEFAULT_W,
  type ClampBounds,
  type PanelPosition,
} from "../killfeed-panel";

const PERMISSION = "view.agent_events";
/** Panel zeigt nur die neuesten paar Zeilen (kompaktes Overlay). */
const KILLFEED_PANEL_LIMIT = 20;

// --- localStorage best-effort (SSR/Privatmodus-sicher) --------------------- #
function loadPosition(serverId: string): PanelPosition | null {
  try {
    return parsePanelPosition(localStorage.getItem(killfeedPanelStorageKey(serverId)));
  } catch {
    return null;
  }
}
function savePosition(serverId: string, pos: PanelPosition): void {
  try {
    localStorage.setItem(killfeedPanelStorageKey(serverId), serializePanelPosition(pos));
  } catch {
    /* ignorieren */
  }
}
function clearPosition(serverId: string): void {
  try {
    localStorage.removeItem(killfeedPanelStorageKey(serverId));
  } catch {
    /* ignorieren */
  }
}

function killfeedLabels(): KillfeedLabels {
  return {
    unknownWeapon: t("agentLive.kfUnknownWeapon"),
    suicide: t("agentLive.kfSuicide"),
    environment: t("agentLive.kfEnvironment"),
  };
}

function KillfeedRow({ item }: { item: KillfeedItem }) {
  const labels = killfeedLabels();
  const cls =
    `kfp-item kfp-item--${item.kind}` + (item.headshot ? " kfp-item--headshot" : "");
  return (
    <li class={cls}>
      <span class="kfp-time">{formatClock(item.ts)}</span>
      <span class="kfp-text">{formatKillfeedText(item, labels)}</span>
      {item.headshot && <span class="kfp-badge">{t("agentLive.kfHeadshot")}</span>}
      {item.weapon && <span class="kfp-weapon">{item.weapon}</span>}
    </li>
  );
}

function statusTone(s: LiveStatus): string {
  if (s === "connected") return "ok";
  if (s === "error") return "down";
  if (s === "connecting") return "warn";
  return "off";
}

export function ServerKillfeedPanel({ serverId }: { serverId: string | null | undefined }) {
  const state = useSignal<AgentLiveState>(initialAgentLiveState());
  // `null` = noch keine gespeicherte/gesetzte Position → Default aus Viewport.
  const pos = useSignal<PanelPosition | null>(null);
  const collapsed = useSignal(false);
  const dragging = useSignal(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const sid = (serverId ?? "").trim();

  if (!sid || !hasPermission(PERMISSION)) return null;

  // Aktuelle Clamp-Grenzen aus echter Panelgröße (Fallback: Defaults).
  function currentBounds(): ClampBounds {
    const el = panelRef.current;
    const panelW = el?.offsetWidth || KILLFEED_PANEL_DEFAULT_W;
    const panelH = el?.offsetHeight || KILLFEED_PANEL_DEFAULT_H;
    const viewportW = typeof window !== "undefined" ? window.innerWidth : 1280;
    const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;
    return { viewportW, viewportH, panelW, panelH };
  }

  function resolvedPos(): PanelPosition {
    return pos.value ?? defaultPanelPosition(currentBounds());
  }

  // SSE-Subscription je Server: bei Serverwechsel neu verbinden, beim Unmount
  // sauber schließen. Server-seitig nach server_id gefiltert (buildLiveUrl).
  useEffect(() => {
    state.value = initialAgentLiveState();
    const url = buildLiveUrl({ serverId: sid, limit: 100 });
    const client = new AgentLiveClient(url, {
      onStatus: (s) => { state.value = applyStatus(state.value, s); },
      onReady: (d) => { state.value = applyReady(state.value, d); },
      onBackfill: (evs) => { state.value = applyBackfill(state.value, evs); },
      onEvent: (ev) => { state.value = applyAgentEvent(state.value, ev, Date.now()); },
      onHeartbeat: (hb) => { state.value = applyHeartbeat(state.value, hb); },
    });
    client.connect();
    return () => client.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  // Position beim Serverwechsel laden (pro Server) + gegen aktuellen Viewport
  // clampen (falls das Fenster seit dem Speichern kleiner wurde).
  useEffect(() => {
    const stored = loadPosition(sid);
    pos.value = stored ? clampPanelPosition(stored, currentBounds()) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  // Bei Fenster-Resize das Panel wieder in den sichtbaren Bereich clampen (nur
  // wenn eine echte Position gesetzt ist; Default rechnet sich selbst neu).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      if (!pos.value) return;
      const clamped = clampPanelPosition(pos.value, currentBounds());
      pos.value = clamped;
      savePosition(sid, clamped);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  function startDrag(e: PointerEvent) {
    if (e.button !== undefined && e.button !== 0) return; // nur linke Maustaste
    e.preventDefault();
    const start = resolvedPos();
    const startX = e.clientX;
    const startY = e.clientY;
    dragging.value = true;
    const onMove = (ev: PointerEvent) => {
      // Während des Drags NICHT clampen (fühlt sich natürlich an) und NICHT
      // speichern (Perf) — beides passiert erst am Drag-Ende.
      pos.value = { x: start.x + (ev.clientX - startX), y: start.y + (ev.clientY - startY) };
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      dragging.value = false;
      const clamped = clampPanelPosition(pos.value ?? start, currentBounds());
      pos.value = clamped;
      savePosition(sid, clamped); // nur am Drag-Ende schreiben
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  }

  function reset() {
    clearPosition(sid);
    pos.value = defaultPanelPosition(currentBounds());
  }

  const st = state.value;
  const filtered = filterEventsForServer(st.events, sid);
  const killfeed = buildKillfeedItems(filtered, { limit: KILLFEED_PANEL_LIMIT });
  const p = resolvedPos();

  return (
    <div
      ref={panelRef}
      class={`kfp ${dragging.value ? "kfp--dragging" : ""} ${collapsed.value ? "kfp--collapsed" : ""}`}
      style={`left:${Math.round(p.x)}px;top:${Math.round(p.y)}px`}
      role="complementary"
      aria-label={t("killfeedPanel.title")}
    >
      <div class="kfp-head" onPointerDown={(ev) => startDrag(ev as unknown as PointerEvent)}>
        <span class={`kfp-status ${statusTone(st.status)}`} title={st.status}><span class="live-dot" /></span>
        <span class="kfp-title">{t("killfeedPanel.title")}</span>
        {/* Buttons nicht als Drag interpretieren. */}
        <div class="kfp-actions" onPointerDown={(ev) => (ev as unknown as PointerEvent).stopPropagation()}>
          <button
            class="kfp-btn"
            type="button"
            onClick={() => (collapsed.value = !collapsed.value)}
            title={collapsed.value ? t("killfeedPanel.expand") : t("killfeedPanel.collapse")}
            aria-label={collapsed.value ? t("killfeedPanel.expand") : t("killfeedPanel.collapse")}
          >
            {collapsed.value ? "▢" : "—"}
          </button>
          <button class="kfp-btn" type="button" onClick={reset} title={t("killfeedPanel.reset")}>
            ↺
          </button>
        </div>
      </div>

      {!collapsed.value && (
        <div class="kfp-body">
          {killfeed.length === 0 ? (
            <div class="kfp-empty">
              {st.status === "error"
                ? t("killfeedPanel.offline")
                : t("killfeedPanel.empty")}
            </div>
          ) : (
            <ul class="kfp-list">
              {killfeed.map((item) => (
                <KillfeedRow key={item.event_id} item={item} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
